// @flow
import {
  differenceInDays,
  subDays,
  addDays,
  addMonths,
  isWithinRange,
  isAfter,
  isSameDay,
  startOfDay,
  endOfDay,
  format,
  isPast,
  isToday,
} from "date-fns"
import { equals, compose, uniq } from "ramda"

import { get } from "common/utils/get"
import set from "common/utils/set"
import { DATE_FORMAT } from "common/consts/date"
import constants from "../../consts/config"
import { DATE_TYPES } from "./SearchDate"
import type { SearchDate, DateRange, TimeToStay, Anytime, NoReturn } from "./SearchDate"

export const getId = (date: SearchDate): string => {
  switch (date.type) {
    case DATE_TYPES.DATE_RANGE:
      return uniq([format(date.from, DATE_FORMAT), format(date.to, DATE_FORMAT)]).join("_")
    case DATE_TYPES.TIME_TO_STAY:
      return uniq([date.min, date.max]).join("-")
    default:
      return date.type
  }
}

export const isDateRange = (date: SearchDate): boolean %checks =>
  date.type === DATE_TYPES.DATE_RANGE

export const isSingleDate = (date: SearchDate): boolean =>
  isDateRange(date) && isSameDay(date.from, date.to)

export const isTimeToStay = (date: SearchDate): boolean %checks =>
  date.type === DATE_TYPES.TIME_TO_STAY

export const isAnytime = (date: SearchDate): boolean %checks => date.type === DATE_TYPES.ANYTIME

export const isNoReturn: SearchDate => boolean = compose(
  equals(DATE_TYPES.NO_RETURN),
  get("type"),
)

export const createRangeAround = (date: Date, dayCount: number = 1): DateRange => {
  const subtract = differenceInDays(date, new Date()) < dayCount ? 0 : dayCount
  return {
    type: DATE_TYPES.DATE_RANGE,
    isDefault: false,
    from: subDays(date, subtract),
    to: addDays(date, dayCount),
  }
}

export const createDateRange = (from: Date, to: Date, isDefault: boolean = false): DateRange => ({
  type: DATE_TYPES.DATE_RANGE,
  isDefault,
  from,
  to,
})

export const createTimeToStay = (
  min: number = 2,
  max: number = 10,
  isDefault: boolean = false,
): TimeToStay => ({
  type: DATE_TYPES.TIME_TO_STAY,
  isDefault,
  min,
  max,
})

export const createNoReturn = (isDefault: boolean = false): NoReturn => ({
  type: DATE_TYPES.NO_RETURN,
  isDefault,
})

export const createAnytime = (isDefault: boolean = false): Anytime => ({
  type: DATE_TYPES.ANYTIME,
  isDefault,
})

const DEFAULT_DATE_RANGE_DATES = 3

export const toDateRange = (from: Date) => ({
  type: DATE_TYPES.DATE_RANGE,
  isDefault: false,
  from,
  to: addDays(from, DEFAULT_DATE_RANGE_DATES),
})

export const nextDate = (lastDate: Date | SearchDate | void): DateRange => {
  let from
  if (lastDate instanceof Date) {
    from = lastDate
  } else if (lastDate && lastDate.type === DATE_TYPES.DATE_RANGE) {
    from = lastDate.to
  } else {
    from = new Date()
  }

  return toDateRange(addDays(from, DEFAULT_DATE_RANGE_DATES))
}

export const getAnytimeRange = (): DateRange => ({
  type: DATE_TYPES.DATE_RANGE,
  isDefault: false,
  from: addDays(new Date(), 1),
  to: addMonths(new Date(), Number(constants.maxSearchMonths)),
})

export const setIsDefault = (value: boolean, date: SearchDate): SearchDate =>
  set("isDefault", value, date)

export const includesDate = (searchDate: SearchDate, date: Date): boolean =>
  searchDate.type === DATE_TYPES.DATE_RANGE &&
  isWithinRange(date, startOfDay(searchDate.from), endOfDay(searchDate.to))

export const dateRangesEqual = (valueA: ?DateRange, valueB: ?DateRange) =>
  !valueA || !valueB
    ? !valueA && !valueB
    : valueA.type === valueB.type &&
      valueA.isDefault === valueB.isDefault &&
      isSameDay(valueA.to, valueB.to) &&
      isSameDay(valueA.from, valueB.from)

// is first after or equal second?
export const isAfterOrEqual = (first: Date, second: Date): boolean =>
  isAfter(first, second) || isSameDay(first, second)

export const isInPast = (date: Date): boolean => isPast(date) && !isToday(date)
